import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  Typography,
  Link,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  anthropic_bodyTemplate,
  anthropic_EndpointTemplate,
  anthropic_headerTemplate,
  azure_bodyTemplate,
  azure_EndpointTemplate,
  azure_headerTemplate,
  google_bodyTemplate,
  google_EndpointTemplate,
  google_headerTemplate,
  mistral_bodyTemplate,
  mistral_EndpointTemplate,
  mistral_headerTemplate,
  openAI_bodyTemplate,
  openAI_EndpointTemplate,
  openAI_headerTemplate,
} from "../modelTemplates";
import { useNavigate } from "react-router-dom";
import { group } from "console";

interface PricingData {
  vendor: string;
  model: string;
  modelDescription: string;
  inputTokenPricePer1M: number | undefined;
  outputTokenPricePer1M: number | undefined;
  group?: string;
  description?: string;
  pricingUrl?: string;
}

const ModelPricing: React.FC = () => {
  const [selectedVendor, setSelectedVendor] = useState<string>("");
  const navigate = useNavigate();

  const pricingData: PricingData[] = [
    {
      // openAI
      vendor: "Open-AI",
      model: "gpt-4o",
      modelDescription: "GPT4o (128K, Oct 2023)",
      inputTokenPricePer1M: 2.5,
      outputTokenPricePer1M: 10,
      group: "premium",
      description:
        "GPT-4o: Our high-intelligence flagship model for complex, multi-step tasks. GPT-4o is cheaper and faster than GPT-4 Turbo.",
      pricingUrl: "https://openai.com/api/pricing/",
    },
    {
      vendor: "Open-AI",
      model: "gpt-4o-mini",
      modelDescription: "gpt-4o-mini (128K, Oct 2023)",
      inputTokenPricePer1M: 0.15,
      outputTokenPricePer1M: 0.6,
      group: "default",
      description:
        "GPT-4o-mini: Our affordable and intelligent small model for fast, lightweight tasks. GPT-4o mini is cheaper and more capable than GPT-3.5 Turbo. Currently points to gpt-4o-mini-2024-07-18.",
      pricingUrl: "https://openai.com/api/pricing/",
    },
    {
      vendor: "Open-AI",
      model: "o1-preview",
      modelDescription: "o1-Preview (128K, Oct 2023)",
      inputTokenPricePer1M: 15,
      outputTokenPricePer1M: 60,
      group: "premium",
      description:
        "The o1 series of large language models are trained with reinforcement learning to perform complex reasoning. o1 models think before they answer, producing a long internal chain of thought before responding to the user.",
      pricingUrl: "https://openai.com/api/pricing/",
    },
    {
      vendor: "Open-AI",
      model: "o1-mini",
      modelDescription: "o1-Mini (128K, Oct 2023)",
      inputTokenPricePer1M: 3,
      outputTokenPricePer1M: 12,
      group: "default",
      description:
        "o1-mini: faster and cheaper reasoning model particularly good at coding, math, and science.",
      pricingUrl: "https://openai.com/api/pricing/",
    },

    // microsoft
    {
      vendor: "Microsoft",
      model: "gpt-4o",
      modelDescription: "GPT-4o Global Deployment (128K)",
      inputTokenPricePer1M: 5,
      outputTokenPricePer1M: 15,
      group: "premium",
      description:
        "GPT-4o: High-intelligence flagship model for complex, multi-step tasks. GPT-4o is cheaper and faster than GPT-4 Turbo.",
      pricingUrl:
        "https://azure.microsoft.com/en-us/pricing/details/cognitive-services/openai-service/",
    },
    {
      vendor: "Microsoft",
      model: "gpt-4o",
      modelDescription: "GPT-4o Regional API (128K)",
      inputTokenPricePer1M: 5,
      outputTokenPricePer1M: 15,
      group: "premium",
      description:
        "GPT-4o: High-intelligence flagship model for complex, multi-step tasks. GPT-4o is cheaper and faster than GPT-4 Turbo.",
      pricingUrl:
        "https://azure.microsoft.com/en-us/pricing/details/cognitive-services/openai-service/",
    },
    {
      vendor: "Microsoft",
      model: "gpt-4o-mini",
      modelDescription: "GPT-4o-mini Global Deployment (128K)",
      inputTokenPricePer1M: 0.16,
      outputTokenPricePer1M: 0.6,
      group: "default",
      description:
        "GPT-4o-mini: Our affordable and intelligent small model for fast, lightweight tasks. GPT-4o mini is cheaper and more capable than GPT-3.5 Turbo.",
      pricingUrl:
        "https://azure.microsoft.com/en-us/pricing/details/cognitive-services/openai-service/",
    },
    {
      vendor: "Microsoft",
      model: "gpt-4o-mini",
      modelDescription: "GPT-4o-mini Regional Deployment (128K)",
      inputTokenPricePer1M: 0.165,
      outputTokenPricePer1M: 0.66,
      group: "default",
      description:
        "GPT-4o-mini: Our affordable and intelligent small model for fast, lightweight tasks. GPT-4o mini is cheaper and more capable than GPT-3.5 Turbo.",
      pricingUrl:
        "https://azure.microsoft.com/en-us/pricing/details/cognitive-services/openai-service/",
    },

    //google

    {
      vendor: "Google",
      model: "gemini-1.5-flash",
      modelDescription: "Gemini 1.5 Flash (128K)",
      inputTokenPricePer1M: 0.075,
      outputTokenPricePer1M: 0.3,
      group: "default",
      description:
        "Gemini 1.5 Flash (up to 128K context window): Our fastest multimodle model with great performance for repetitive tasks.",
      pricingUrl: "https://ai.google.dev/pricing",
    },
    {
      vendor: "Google",
      model: "gemini-1.5-flash",
      modelDescription: "Gemini 1.5 Flash (>128K)",
      inputTokenPricePer1M: 0.15,
      outputTokenPricePer1M: 0.6,
      group: "default",
      description:
        "Gemini 1.5 Flash (greater than 128K context window): Our fastest multimodle model with great performance for repetitive tasks.",
      pricingUrl: "https://ai.google.dev/pricing",
    },
    {
      vendor: "Google",
      model: "gemini-1.5-pro",
      modelDescription:
        "Gemini 1.5 Pro (2M context window, up to 128K prompt length)",
      inputTokenPricePer1M: 3.5,
      outputTokenPricePer1M: 10.5,
      group: "premium",
      description:
        "Gemini 1.5 Pro: Our most powerful multimodal model with the largest context window and prompt length.",
      pricingUrl: "https://ai.google.dev/pricing",
    },
    {
      vendor: "Google",
      model: "gemini-1.5-pro",
      modelDescription: "Gemini 1.5 Pro (2M context window, , >128K)",
      inputTokenPricePer1M: 7.0,
      outputTokenPricePer1M: 21.0,
      group: "premium",
      description:
        "Gemini 1.5 Pro: Our most powerful multimodal model with the largest context window and prompt length.",
      pricingUrl: "https://ai.google.dev/pricing",
    },

    // anthropic
    {
      vendor: "Anthropic",
      model: "claude-3-5-sonnet-20240620",
      modelDescription: "Claude 3.5 Sonnet (200K)",
      inputTokenPricePer1M: 3,
      outputTokenPricePer1M: 15,
      group: "premium",
      description: "Claude 3.5 Sonnet: Our most intelligent model to date.",
      pricingUrl: "https://www.anthropic.com/pricing#anthropic-api",
    },
    {
      vendor: "Anthropic",
      model: "claude-3-opus-20240229",
      modelDescription: "Claude 3 Opus (200K)",
      inputTokenPricePer1M: 15,
      outputTokenPricePer1M: 75,
      group: "premium",
      description: "Claude 3 Opus: Powerful model for complex tasks.",
      pricingUrl: "https://www.anthropic.com/pricing#anthropic-api",
    },
    {
      vendor: "Anthropic",
      model: "claude-3-haiku-20240307",
      modelDescription: "Claude 3 Haiku (200K)",
      inputTokenPricePer1M: 0.25,
      outputTokenPricePer1M: 1.25,
      group: "default",
      description: "Claude 3 Haiku: Fastest, most cost effective model.",
      pricingUrl: "https://www.anthropic.com/pricing#anthropic-api",
    },

    // mistral
    {
      vendor: "Mistral",
      model: "open-mistral-nemo",
      modelDescription: "Mistral Nemo",
      inputTokenPricePer1M: 0.3,
      outputTokenPricePer1M: 0.3,
      group: "default",
      description:
        "Mistral Nemo is a state-of-the-art 12B model developed with NVIDIA.",
      pricingUrl: "https://mistral.ai/technology/",
    },
    {
      vendor: "Mistral",
      model: "mistral-large-latest",
      modelDescription: "Mistral Large 2",
      inputTokenPricePer1M: 3,
      outputTokenPricePer1M: 9,
      group: "premium",
      description:
        "Top-tier reasoning for high-complexity tasks, for your most sophisticated needs",
      pricingUrl: "https://mistral.ai/technology/",
    },
  ];

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

  const handleVendorChange = (event: SelectChangeEvent<string>) => {
    setSelectedVendor(event.target.value as string);
  };

  const filteredPricingData = selectedVendor
    ? pricingData.filter((data) => data.vendor === selectedVendor)
    : pricingData;

  const uniqueVendors = Array.from(
    new Set(pricingData.map((data) => data.vendor))
  );

  const getEndpoint = (vendor: string, model: string): string => {
    switch (vendor) {
      case "Open-AI":
        return openAI_EndpointTemplate;
      case "Microsoft":
        return azure_EndpointTemplate;
      case "Google":
        return google_EndpointTemplate.replace("gemini-1.5-flash", model);
      case "Anthropic":
        return anthropic_EndpointTemplate;
      case "Mistral":
        return mistral_EndpointTemplate;
      default:
        return "";
    }
  };

  const getHeader = (vendor: string, model: string): string => {
    switch (vendor) {
      case "Open-AI":
        return openAI_headerTemplate;
      case "Microsoft":
        return azure_headerTemplate;
      case "Google":
        return google_headerTemplate;
      case "Anthropic":
        return anthropic_headerTemplate;
      case "Mistral":
        return mistral_headerTemplate;

      default:
        return "";
    }
  };

  const getBody = (vendor: string, model: string): string => {
    let body;
    switch (vendor) {
      case "Open-AI":
        body = JSON.parse(openAI_bodyTemplate);
        body.model = model;
        return JSON.stringify(body, null, 2);
      case "Microsoft":
        body = JSON.parse(openAI_bodyTemplate);
        body.model = model;
        return JSON.stringify(body, null, 2);
      case "Google":
        return google_bodyTemplate;
      case "Anthropic":
        body = JSON.parse(anthropic_bodyTemplate);
        body.model = model;
        return JSON.stringify(body, null, 2);
      case "Mistral":
        body = JSON.parse(mistral_bodyTemplate);
        body.model = model;
        return JSON.stringify(body, null, 2);
      default:
        return "";
    }
  };

  const getVendor = (vendor: string): string => {
    switch (vendor) {
      case "Open-AI":
        return "openai";
      case "Microsoft":
        return "microsoft";
      case "Google":
        return "google";
      case "Anthropic":
        return "anthropic";
      case "Amazon":
        return "amazon";
      case "Mistral":
        return "mistral";
      default:
        return "";
    }
  };

  function handleAdd(pricingData: PricingData): void {
    const data = {
      name: pricingData.modelDescription,
      vendor: getVendor(pricingData.vendor),
      model: pricingData.model,
      endpoint: getEndpoint(pricingData.vendor, pricingData.model),
      header: getHeader(pricingData.vendor, pricingData.model),
      body: getBody(pricingData.vendor, pricingData.model),
      inputprice: pricingData.inputTokenPricePer1M,
      outputprice: pricingData.outputTokenPricePer1M,
      group: pricingData.group,
    };

    console.log(data);
    navigate("/services/new", { state: { data } });
  }

  return (
    <Box m={2}>
      <Typography variant="h6" gutterBottom>
        Select a model to add, or <Button variant="text" color="primary" onClick={() => navigate("/services/new")}>Start Blank Model</Button>
      </Typography>
      <FormControl
        variant="outlined"
        sx={{ width: "150px" }}
        margin="normal"
        size="small"
      >
        <InputLabel id="vendor-select-label">Vendor</InputLabel>
        <Select
          labelId="vendor-select-label"
          value={selectedVendor}
          onChange={handleVendorChange}
          label="Vendor"
        >
          <MenuItem value="">
            <em>All Vendors</em>
          </MenuItem>
          {uniqueVendors.map((vendor) => (
            <MenuItem key={vendor} value={vendor}>
              {vendor}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <List>
        {filteredPricingData.map((pricingData) => (
          <ListItem>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAdd(pricingData)}
              sx={{ marginRight: "10px" }}
            >
              add
            </Button>
            <ListItemText
              primary={`${pricingData.modelDescription} by ${pricingData.vendor} (input: $${pricingData.inputTokenPricePer1M} / output: $${pricingData.outputTokenPricePer1M} per million tokens)` }
              secondary={
                <Typography variant="body2" color="textSecondary">
                  {pricingData.description}{" "}
                  <Link
                    href={pricingData.pricingUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    see current pricing
                  </Link>
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ModelPricing;
